// CodexView — the discovery codex, rendered blindly from vsj.codex. ONE table
// for all KNOWN animals: a single sticky header row of statvocab column
// headers, then one tbody per beast with its icon/name/tier shown once
// (rowspan) beside two role rows — "prefix" (as_first modifier deltas) and
// "base" (as_second body-plan profile). An unseen half arrives null and
// renders as a greyed "not yet seen" row. Served stat keys drive the columns
// (preferred order first, unknown keys appended); values stay pre-signed
// server strings. Beasts ONLY — no craft/location listings.

import { clear, el, tooltip } from '../kit';
import * as icons from '../icons';
import * as vsj from '../vsj';
import { STATS, statColor } from '../statvocab';

// preferred column order; any other served stat key is appended after these
const COL_ORDER = ['hp', 'dmg', 'swt', 'acc', 'agi', 'arm', 'res', 'spd'];

export function codexView(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const head = el('div', { class: 'k-small k-faint', style: 'padding:8px 10px 6px' });
  const scroller = el('div', { class: 'k-scroll codex-scroll' });
  const root = el(
    'div',
    { class: 'k-panel', style: 'flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden' },
    head,
    scroller,
  );
  let sig = '';

  // column list = union of served stat keys across every seen half
  const columns = (animals: any): string[] => {
    const seen = new Set<string>();
    for (const id of Object.keys(animals))
      for (const half of [animals[id]?.as_first, animals[id]?.as_second])
        if (half && Array.isArray(half.rows))
          for (const r of half.rows) if (r && r.stat != null) seen.add(String(r.stat));
    return [...COL_ORDER.filter((k) => seen.has(k)), ...[...seen].filter((k) => !COL_ORDER.includes(k))];
  };

  const headerRow = (cols: string[]): HTMLElement => {
    const tr = el('tr', {}, el('th', { class: 'codex-beast', text: 'Beast' }), el('th'));
    for (const key of cols) {
      const th = el('th', {
        class: 'codex-val',
        style: `color:${statColor(key)}`,
        text: STATS[key]?.abbr ?? key,
      });
      tooltip(th, () => STATS[key]?.full ?? key);
      tr.append(th);
    }
    tr.append(el('th'));
    return tr;
  };

  // one role row: label cell + a value cell per column (blank where the half
  // served no row for that stat) + a notes cell of served prose, verbatim
  const roleRow = (label: string, tip: string, half: any, roleWord: string, cols: string[]): HTMLElement => {
    const tr = el('tr');
    const lab = el('td', { class: 'codex-role', text: label });
    tooltip(lab, () => tip);
    tr.append(lab);
    if (!half || typeof half !== 'object') {
      tr.append(
        el('td', { class: 'codex-unseen', colspan: String(cols.length + 1), text: `Not yet seen as the ${roleWord} animal.` }),
      );
      return tr;
    }
    const byStat: Record<string, unknown> = {};
    if (Array.isArray(half.rows))
      for (const r of half.rows) if (r && r.stat != null && r.value != null) byStat[String(r.stat)] = r.value;
    for (const key of cols)
      tr.append(el('td', { class: 'codex-val', text: byStat[key] != null ? String(byStat[key]) : '' }));
    const notes = ['note', 'dmg_type', 'drops_text']
      .map((k) => half[k])
      .filter((v) => v != null)
      .map(String);
    tr.append(el('td', { class: 'codex-notes', text: notes.join(' · ') }));
    return tr;
  };

  // one tbody per beast: icon/name/tier/tags ONCE (rowspan 2), then its two
  // role rows
  const animalBody = (id: string, a: any, cols: string[]): HTMLElement => {
    const tags = ['diet', 'genus'].map((k) => a[k]).filter((v) => v != null);
    const beast = el(
      'td',
      { class: 'codex-beast', rowspan: '2' },
      el(
        'div',
        { class: 'codex-name' },
        icons.texture('animal_' + id, 30),
        el(
          'div',
          { style: 'display:flex;flex-direction:column;gap:1px' },
          el('span', { class: 'c-creature', style: 'font-weight:600', text: String(a.name ?? id) }),
          el(
            'div',
            { style: 'display:flex;align-items:center;gap:6px' },
            icons.tierBadge(String(a.tier ?? '')),
            tags.length ? el('span', { class: 'k-small k-faint', text: tags.join(' · ') }) : null,
          ),
        ),
      ),
    );
    const first = roleRow('prefix', 'As the modifier (first) animal — flat deltas', a.as_first, 'modifier', cols);
    first.prepend(beast);
    return el(
      'tbody',
      {},
      first,
      roleRow('base', 'As the base (second) animal — the body plan', a.as_second, 'base', cols),
    );
  };

  const refresh = () => {
    const codex = vsj.at('codex') ?? {};
    const s = JSON.stringify(codex);
    if (s === sig) return;
    sig = s;
    clear(scroller);

    const animals = codex.animals ?? {};
    const ids = Object.keys(animals).filter((id) => animals[id] && typeof animals[id] === 'object');
    head.textContent = ids.length ? `Beasts · ${ids.length} known` : 'Beasts';
    if (!ids.length) {
      scroller.append(
        el('div', { class: 'k-small k-faint', style: 'padding:10px', text: "Join a fight to learn its beast's animals." }),
      );
      return;
    }
    // sort by the SERVED tier_index (the ladder lives on the driver)
    ids.sort((a, b) => {
      const ta = Number(animals[a]?.tier_index ?? 0);
      const tb = Number(animals[b]?.tier_index ?? 0);
      return ta !== tb ? ta - tb : a < b ? -1 : 1;
    });
    const cols = columns(animals);
    const table = el('table', { class: 'codex-table' }, el('thead', {}, headerRow(cols)));
    for (const id of ids) table.append(animalBody(id, animals[id], cols));
    scroller.append(table);
  };
  refresh();

  return {
    root,
    refresh(changed) {
      if (changed.has('codex') || changed.has('*')) refresh();
    },
  };
}
